/**
 * Reconcile results — preview → restore dialog.
 * scope: { batchId, batchName } for one batch, or { org: true } for every batch.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCcw } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

const MATCH = { 'former-record': 'former record', email: 'email', 'employee-id': 'employee ID' };

function AgentList({ items }) {
  return (
    <div className="max-h-72 overflow-y-auto border rounded-md divide-y" data-testid="reconcile-agent-list">
      {items.map(g => (
        <div key={g.trainee_id} className="px-3 py-2" data-testid={`reconcile-agent-${g.trainee_id}`}>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium truncate">{g.name || g.email}</span>
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">matched by {MATCH[g.match] || g.match}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {g.assessments.map(a => `${a.name}${a.best != null ? ` (${Math.round(a.best)}%)` : ''}`).join(', ')}
          </p>
        </div>
      ))}
    </div>
  );
}

function Summary({ pv, org }) {
  const n = pv.uncredited, a = pv.agents;
  if (n === 0) {
    return (
      <p className="text-sm" data-testid="reconcile-count-line">
        Every submitted result{org ? ' in this org' : ' for this batch'} is already credited to an agent on the roster.
      </p>
    );
  }
  return (
    <p className="text-sm" data-testid="reconcile-count-line">
      <span className="font-medium">{n} submission{n === 1 ? '' : 's'}</span> for{' '}
      <span className="font-medium">{a} agent{a === 1 ? '' : 's'}</span> {n === 1 ? 'is' : 'are'} not credited
      {org ? ` across ${pv.batches.length} batch${pv.batches.length === 1 ? '' : 'es'}` : ' to this batch'} — restore {n === 1 ? 'it' : 'them'}?
    </p>
  );
}

export function ReconcileResultsDialog({ open, onOpenChange, scope, onDone }) {
  const { get, post } = useApi();
  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const org = !!scope?.org;
  const url = org ? '/trainees/reconcile/org' : `/trainees/reconcile/batch/${scope?.batchId}`;

  useEffect(() => {
    if (!open) { setPv(null); setResult(null); return; }
    setLoading(true);
    get(url).then(setPv).catch(e => toast.error(e.response?.data?.detail || 'Could not scan results'))
      .finally(() => setLoading(false));
  }, [open, url, get]);

  const items = pv ? (org ? pv.batches.flatMap(b => b.items.map(i => ({ ...i, trainee_id: `${b.batch_id}:${i.trainee_id}`, name: `${i.name} · ${b.batch_name}` }))) : pv.items) : [];
  const unmatched = pv ? (org ? pv.batches.flatMap(b => b.unmatched) : pv.unmatched) : [];

  const restore = async () => {
    setBusy(true);
    try {
      const r = await post(url, {});
      setResult(r);
      toast.success(`${r.restored} submission${r.restored === 1 ? '' : 's'} restored`);
      onDone?.(r);
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle>Reconcile results{scope?.batchName ? ` — ${scope.batchName}` : ''}</DialogTitle>
          <DialogDescription>
            Finds submitted online assessments not credited to an agent on the roster and re-attaches them.
            Nothing changes until you confirm.
          </DialogDescription>
        </DialogHeader>
        {loading && <p className="text-sm text-muted-foreground" data-testid="reconcile-loading">Scanning submissions…</p>}
        {!loading && pv && !result && (
          <>
            <Summary pv={pv} org={org} />
            {items.length > 0 && <AgentList items={items} />}
            {unmatched.length > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="reconcile-unmatched-note">
                {unmatched.reduce((s, u) => s + (u.attempts || 0), 0)} submission(s) from {unmatched.length} record(s) could not be matched
                to anyone on the roster{unmatched.some(u => u.name_only) ? ' (name only — not restored automatically)' : ''}.
              </p>
            )}
          </>
        )}
        {result && (
          <div className="space-y-2" data-testid="reconcile-report">
            <p className="text-sm">
              <span className="font-medium">{result.restored} submission{result.restored === 1 ? '' : 's'}</span> restored for{' '}
              <span className="font-medium">{result.agents} agent{result.agents === 1 ? '' : 's'}</span>. Results, chips, score columns and the leaderboard are updated.
            </p>
            {!org && result.report?.items?.length > 0 && (
              <AgentList items={result.report.items.map(i => ({ ...i, assessments: i.assessments }))} />
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reconcile-cancel-btn">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={restore} disabled={busy || loading || !pv || pv.uncredited === 0} data-testid="reconcile-restore-btn">
              <RefreshCcw className="w-4 h-4 mr-2" /> {busy ? 'Restoring…' : 'Restore'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
